import { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import Autocomplete from "@/components/Autocomplete";
import AvatarDetails from "@/components/AvatarDetails";
import Icon from "@/components/Icon";
import PhotoboothEmptyState from "./PhotoboothEmptyState";
import { PHOTOBOOTH_PERSON_SEARCH_QUERY, PhotoboothPerson } from "../queries";

interface EntryPageProps {
  onSelectPerson: (person: PhotoboothPerson) => void;
}

export default function EntryPage({ onSelectPerson }: EntryPageProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  const { data, loading } = useQuery(PHOTOBOOTH_PERSON_SEARCH_QUERY, {
    variables: {
      input: { search: debouncedSearch },
      first: 10
    },
    skip: !debouncedSearch || debouncedSearch.trim() === ""
  });

  const results = data?.people?.nodes || [];
  const totalCount = data?.people?.totalCount || 0;

  const getPersonLocation = (person: PhotoboothPerson): string => {
    const homeAddress = person.addresses?.find((addr) => addr.addressType === "HOME");
    const anyAddress = person.addresses?.[0];
    const address = homeAddress || anyAddress;

    if (address?.city && address?.state) {
      return `${address.city}, ${address.state}`;
    }
    return "";
  };

  return (
    <div className="max-w-lg mx-auto py-6">
      {/* Header */}
      <div className="mb-6">
        <span className="text-label-small text-sys-brand-primary font-semibold tracking-wider uppercase">BETA</span>
        <h1 className="text-headline-large-bold font-serif text-sys-brand-on-surface">Photobooth</h1>
      </div>

      {/* Search Input */}
      <div className="mb-6">
        <Autocomplete
          label=""
          placeholder="Search name or company"
          query={searchQuery}
          loadingResults={loading}
          data={results}
          onChangeText={setSearchQuery}
          reset={() => setSearchQuery("")}
          onSelect={onSelectPerson}
          renderItem={(person: PhotoboothPerson) => (
            <div className="flex items-center justify-between w-full">
              <AvatarDetails
                picture={person.profilePhoto}
                title={`${person.firstName} ${person.lastName}`}
                text={getPersonLocation(person)}
                secondaryText={person.pgaId ? `PGA ID: ${person.pgaId}` : ""}
              />
              <Icon name="chevron_right" className="text-sys-brand-on-surface-variant" />
            </div>
          )}
          menuClassName="max-h-[400px]"
          data-testid="person-search"
        />
      </div>

      {/* Results Section */}
      {debouncedSearch && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-label-small text-sys-brand-on-surface-variant font-medium tracking-wider uppercase">
              MATCHING RESULTS
            </span>
            <span className="text-label-medium text-sys-brand-primary">
              {totalCount} {totalCount === 1 ? "Result" : "Results"} Found
            </span>
          </div>

          {/* Empty state when no results found */}
          {!loading && results.length === 0 && debouncedSearch && <PhotoboothEmptyState />}
        </div>
      )}
    </div>
  );
}
